import {
    useQuery,
    useMutation,
    useQueryClient
} from 'react-query';

import {
    useCallback
} from 'react';

import {
    useGetAccountId,
    useGetLandscapeId
} from '../../../hooks/widgetConfiguration.js';

import useMetrics from '../../../hooks/useMetrics.js';

async function saveLandscapeListeningQueryAsync(landscapeId, listeningQueryId, {name, definition, companyId, listeningQueryTypeId, updateLastViewed}) {
    let res;

    if (!listeningQueryId) {
        res = await $.ajax({
            contentType: 'application/json',
            url: `/api/landscape/${landscapeId}/listeningQueries`,
            type: 'POST',
            data: JSON.stringify({
                name: name,
                definition: definition,
                companyId: companyId,
                listeningQueryTypeId: listeningQueryTypeId
            })
        });
    } else {
        res = await $.ajax({
            contentType: 'application/json',
            url: `/api/landscape/${landscapeId}/listeningQueries/${listeningQueryId}`,
            type: 'PUT',
            data: JSON.stringify({
                name: name,
                definition: definition,
                updateLastViewed: updateLastViewed
            })
        });
    }

    return res;
}

async function deleteLandscapeListeningQueryAsync(landscapeId, listeningQueryId) {
    let res;

    if (listeningQueryId) {
        res = await $.ajax({
            url: `/api/landscape/${landscapeId}/listeningQueries/${listeningQueryId}`,
            type: 'DELETE'
        });
    }

    return res;
}

function useListListeningQueries_internal(useQueryOptions) {
    const accountId = useGetAccountId();
    return useQuery(['accountListeningQuery'], async () => {
        const listeningQueries = await $.getJSON(`/api/account/${accountId}/listeningQueries`);

        return listeningQueries;
    }, {
        ...useQueryOptions,
        staleTime: Infinity
    });
}

// Hook to load a single landscape-level (or shared) listeningQuery for this landscape from the server
export function useLoadLandscapeListeningQuery(listeningQueryId) {
    const landscapeId = useGetLandscapeId();
    const accountListeningQuery = useListListeningQueries_internal({
        select: listeningQueries => {
            return _.find(listeningQueries, {
                id: Number(listeningQueryId),
                landscapeId: landscapeId
            });
        }
    });
    const sampleTopic = useLoadSampleListeningQuery(listeningQueryId);

    return accountListeningQuery?.data ? accountListeningQuery : sampleTopic;
}

// Hook to persist a single landscape-level listeningQuery to the server. This returns a function
// that you can call later with data with a name and a definition.
export function useSaveLandscapeListeningQuery(listeningQueryId, keepCache) {
    const queryClient = useQueryClient();
    const landscapeId = useGetLandscapeId();

    return useMutation(data => saveLandscapeListeningQueryAsync(landscapeId, listeningQueryId, data), {
        onSuccess: (updatedListeningQuery) => {
            // remove existing metric queries since they might be invalid now.
            if (!keepCache) {
                RivalIQ.metrics.new.client.CacheLayer.invalidateCaches();
                queryClient.resetQueries('metrics');
            }

            queryClient.setQueryData(['accountListeningQuery'], existingListeningQueries => {
                const updatedListeningQueries = _.map(existingListeningQueries, existingListeningQueries => {
                    if (existingListeningQueries.id === Number(listeningQueryId)) {
                        return updatedListeningQuery;
                    } else {
                        return existingListeningQueries;
                    }
                });

                if (!listeningQueryId) {
                    updatedListeningQueries.push(updatedListeningQuery);
                }

                return updatedListeningQueries;
            });
        }
    });
}

// Hook to update the last_viewed timestamp for a listeningQuery.
export function useUpdateListeningQueryLastViewed(listeningQueryId, name, definition) {

    const {mutate} = useSaveLandscapeListeningQuery(listeningQueryId, true);

    const updateLastViewed = useCallback(() => {
        if (listeningQueryId) {
            mutate({
                name: name,
                definition: definition,
                updateLastViewed: true
            });
        }
    }, [listeningQueryId, definition, mutate, name]);

    return {
        mutate,
        updateLastViewed
    };
}

// Hook to delete a landscape-level listeningQuery.
export function useDeleteLandscapeListeningQuery(listeningQueryId, keepCache) {
    const queryClient = useQueryClient();
    const landscapeId = useGetLandscapeId();

    return useMutation(data => deleteLandscapeListeningQueryAsync(landscapeId, listeningQueryId, data), {
        onSettled: () => {

            // remove existing metric queries since they might be invalid now.
            if (!keepCache) {
                RivalIQ.metrics.new.client.CacheLayer.invalidateCaches();
                queryClient.resetQueries('metrics');
            }

            queryClient.setQueryData(['accountListeningQuery'], existingListeningQueries => {
                return _.reject(existingListeningQueries, {
                    id: listeningQueryId
                });
            });
        }
    });
}

// Hook to get all of the savedSearches for the current account
export function useListAccountSavedSearches() {

    return useListListeningQueries_internal({
        select: savedSearches => {
            return _.filter(savedSearches, {
                listeningQueryTypeId: RivalIQ.enums.listeningQueryTypes.SavedSearch.id
            });
        }
    });
}

// Hook to get all of the QuidTopics for the current account
export function useListAccountQuidTopics() {

    return useListListeningQueries_internal({
        select: listeningQueries => {
            return _.filter(listeningQueries, {
                listeningQueryTypeId: RivalIQ.enums.listeningQueryTypes.QuidTopic.id
            });
        }
    });
}

// Hook to get all of the landscape-level savedSearches for a given landscapeId.
export function useListLandscapeSavedSearches() {
    const landscapeId = useGetLandscapeId();

    return useListListeningQueries_internal({
        select: savedSearches => {
            return _.filter(savedSearches, {
                listeningQueryTypeId: RivalIQ.enums.listeningQueryTypes.SavedSearch.id,
                landscapeId: landscapeId
            });
        }
    });
}

// Hook to get all of the landscape-level quid topics for a given landscapeId.
export function useListLandscapeQuidTopics() {
    const landscapeId = useGetLandscapeId();

    return useListListeningQueries_internal({
        select: listeningQueries => {
            return _.filter(listeningQueries, {
                listeningQueryTypeId: RivalIQ.enums.listeningQueryTypes.QuidTopic.id,
                landscapeId: landscapeId
            });
        }
    });
}

// Hook to get all of the sample listeningQueries
export function useListSampleListeningQueries(useQueryOptions) {
    const accountId = useGetAccountId();
    return useQuery(['sampleTopic'], async () => {
        const configuration = await $.getJSON(`/api/account/${accountId}/sampleListeningQueries`);
        return configuration;
    }, {
        ...useQueryOptions,
        staleTime: Infinity
    });
}

export function useLoadSampleListeningQuery(listeningQueryId) {
    const sampleTopics = useListSampleListeningQueries({
        select: listeningQueries => {
            return _.find(listeningQueries, {
                id: Number(listeningQueryId)
            });
        }
    });

    return sampleTopics;
}

function isSavedSearch(listeningQuery) {
    return (listeningQuery?.listeningQueryTypeId === RivalIQ.enums.listeningQueryTypes.SavedSearch.id);
}

function isQuidTopic(listeningQuery) {
    return (listeningQuery?.listeningQueryTypeId === RivalIQ.enums.listeningQueryTypes.QuidTopic.id);
}

export function useGetListeningQueryTimePeriod(listeningQuery, listeningQueryId, timezone) {
    const metricRequests = {};

    if (isQuidTopic(listeningQuery)) {
        metricRequests.topicTimePeriod = {
            dataSourceId: RivalIQ.metrics.new.enums.DataSourceIds.QUID_TOPICS,
            dimensions: ['listeningQueryId'],
            measure: 'topicTimePeriod',
            filters: {
                listening: {
                    listeningQueryId: Number(listeningQueryId)
                },
                timezone: {
                    name: timezone
                }
            }
        };
    }

    const response = useMetrics(metricRequests);

    const {data: metricResults} = response;
    const timePeriod = metricResults?.topicTimePeriod?.data?.[0]?.[1];

    return _.extend(response, {
        data: (timePeriod ? JSON.parse(timePeriod) : null)
    });
}

// Hook to get the topic name from Quid
export function useGetListeningQueryDisplayName(listeningQuery, listeningQueryId) {
    const metricRequests = {};

    if (isQuidTopic(listeningQuery)) {
        metricRequests.topicName = {
            dataSourceId: RivalIQ.metrics.new.enums.DataSourceIds.QUID_TOPICS,
            dimensions: ['listeningQueryId'],
            measure: 'topicName',
            filters: {
                listening: {
                    listeningQueryId: Number(listeningQueryId)
                }
            }
        };
    }

    const {data: metricResults} = useMetrics(metricRequests, {
        requestGroup: 'topicName'
    });

    if (isSavedSearch(listeningQuery)) {
        return listeningQuery.name;
    }

    const topicName = metricResults?.topicName?.data?.[0]?.[1] || '';

    return topicName;
}
